import { useCallback, useMemo, useRef, useState } from 'react';

interface TimelineScrubberProps {
  minTime: number;
  maxTime: number;
  currentTime: number | null; // null = show all (handle at end)
  onChange: (time: number) => void;
  onReset: () => void;
  pixelTimestamps: number[];
}

function formatTime(ms: number): string {
  const date = new Date(ms);
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: 'UTC',
  });
}

const BUCKET_COUNT = 96; // 15-min intervals over ~24h

export function TimelineScrubber({
  minTime,
  maxTime,
  currentTime,
  onChange,
  onReset,
  pixelTimestamps,
}: TimelineScrubberProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const timeRange = maxTime - minTime;
  const isAtEnd = currentTime === null;
  const effectiveTime = currentTime ?? maxTime;
  const progress =
    timeRange > 0 ? Math.max(0, Math.min(100, ((effectiveTime - minTime) / timeRange) * 100)) : 100;

  // Visible pixel count via binary search on sorted timestamps
  const visibleCount = useMemo(() => {
    if (isAtEnd) return pixelTimestamps.length;
    let lo = 0;
    let hi = pixelTimestamps.length;
    while (lo < hi) {
      const mid = (lo + hi) >>> 1;
      if ((pixelTimestamps[mid] ?? 0) <= effectiveTime) lo = mid + 1;
      else hi = mid;
    }
    return lo;
  }, [pixelTimestamps, effectiveTime, isAtEnd]);

  // Activity histogram buckets
  const { buckets, maxBucket } = useMemo(() => {
    const b = new Array<number>(BUCKET_COUNT).fill(0);
    if (timeRange <= 0) return { buckets: b, maxBucket: 1 };
    for (const ts of pixelTimestamps) {
      const idx = Math.min(
        BUCKET_COUNT - 1,
        Math.floor(((ts - minTime) / timeRange) * BUCKET_COUNT)
      );
      const count = b[idx] ?? 0;
      b[idx] = count + 1;
    }
    return { buckets: b, maxBucket: Math.max(1, ...b) };
  }, [pixelTimestamps, minTime, timeRange]);

  const getTimeFromClientX = useCallback(
    (clientX: number) => {
      const track = trackRef.current;
      if (!track) return effectiveTime;
      const rect = track.getBoundingClientRect();
      const fraction = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      return minTime + fraction * timeRange;
    },
    [minTime, timeRange, effectiveTime]
  );

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      setIsDragging(true);
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
      onChange(getTimeFromClientX(e.clientX));
    },
    [getTimeFromClientX, onChange]
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!isDragging) return;
      onChange(getTimeFromClientX(e.clientX));
    },
    [isDragging, getTimeFromClientX, onChange]
  );

  const handlePointerUp = useCallback(
    (e: React.PointerEvent) => {
      if (!isDragging) return;
      setIsDragging(false);
      // Snap to "show all" if dragged near the end
      const time = getTimeFromClientX(e.clientX);
      if (time >= maxTime - timeRange * 0.02) {
        onReset();
      }
    },
    [isDragging, getTimeFromClientX, maxTime, timeRange, onReset]
  );

  // Hour markers for the x-axis
  const hourMarkers = useMemo(() => {
    if (timeRange <= 0) return [];
    const markers: { label: string; position: number }[] = [];

    const firstHour = new Date(minTime);
    firstHour.setUTCMinutes(0, 0, 0);
    if (firstHour.getTime() <= minTime) {
      firstHour.setUTCHours(firstHour.getUTCHours() + 1);
    }

    const hours = timeRange / (1000 * 60 * 60);
    const interval = hours > 18 ? 3 : hours > 8 ? 2 : 1;

    for (let t = firstHour.getTime(); t <= maxTime; t += interval * 60 * 60 * 1000) {
      const pos = ((t - minTime) / timeRange) * 100;
      if (pos >= 8 && pos <= 92) {
        const d = new Date(t);
        markers.push({
          label: d.toLocaleTimeString('en-US', {
            hour: 'numeric',
            hour12: true,
            timeZone: 'UTC',
          }),
          position: pos,
        });
      }
    }
    return markers;
  }, [minTime, maxTime, timeRange]);

  if (pixelTimestamps.length === 0) return null;

  return (
    <div className="shrink-0 px-3 py-2 sm:px-4 sm:py-3 bg-wall-surface/30 border-t border-wall-border/30 select-none">
      {/* Header — compact on mobile */}
      <div className="flex items-center justify-between gap-2 mb-1 sm:mb-2">
        <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
          <svg
            className="w-3 h-3 sm:w-3.5 sm:h-3.5 shrink-0 text-wall-accent"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span className="text-[10px] sm:text-xs font-medium text-wall-muted uppercase tracking-wider truncate">
            Timeline
          </span>
          {!isAtEnd && (
            <span className="text-[10px] sm:text-xs font-mono text-wall-accent shrink-0">
              {formatTime(effectiveTime)}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          <span className="hidden sm:inline text-xs text-wall-muted tabular-nums">
            {visibleCount.toLocaleString()} / {pixelTimestamps.length.toLocaleString()} pixels
          </span>
          <span className="sm:hidden text-[10px] text-wall-muted tabular-nums">
            {visibleCount.toLocaleString()}/{pixelTimestamps.length.toLocaleString()}
          </span>
          {!isAtEnd && (
            <button
              onClick={onReset}
              className="text-[10px] sm:text-xs text-wall-accent hover:text-wall-accent-hover transition-colors font-medium px-1.5 py-0.5 sm:px-2 rounded bg-wall-accent/10 hover:bg-wall-accent/20"
            >
              Show All
            </button>
          )}
        </div>
      </div>

      {/* Activity histogram — hidden on mobile to save space */}
      <div className="hidden sm:flex items-end h-8 gap-px mb-1">
        {buckets.map((count, i) => {
          const height = count > 0 ? Math.max(2, (count / maxBucket) * 32) : 0;
          const bucketEnd = ((i + 1) / BUCKET_COUNT) * 100;
          const isFilled = bucketEnd <= progress;
          return (
            <div
              key={i}
              className={`flex-1 rounded-t-sm transition-colors duration-75 ${
                isFilled ? 'bg-wall-accent/50' : 'bg-wall-border/30'
              }`}
              style={{ height: `${height}px` }}
            />
          );
        })}
      </div>

      {/* Slider track — thinner on mobile */}
      <div
        ref={trackRef}
        className="relative h-2 sm:h-3 bg-wall-border/40 rounded-full cursor-pointer"
        style={{ touchAction: 'none' }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
      >
        {/* Filled portion */}
        <div
          className="absolute inset-y-0 left-0 bg-linear-to-r from-wall-accent to-wall-accent-secondary rounded-full pointer-events-none"
          style={{ width: `${progress}%` }}
        />
        {/* Handle — smaller on mobile for easier tap target without taking space */}
        <div className="absolute top-1/2 pointer-events-none" style={{ left: `${progress}%` }}>
          <div
            className={`w-3.5 h-3.5 sm:w-4 sm:h-4 -translate-x-1/2 -translate-y-1/2 bg-white rounded-full shadow-md shadow-black/30 border-2 border-wall-accent transition-transform ${
              isDragging ? 'scale-125' : ''
            }`}
          />
        </div>
      </div>

      {/* Time labels — single row, smaller on mobile */}
      <div className="relative h-3 sm:h-4 mt-1 sm:mt-1.5">
        <span className="absolute left-0 text-[9px] sm:text-[10px] text-wall-muted/70">
          {formatTime(minTime)}
        </span>
        {hourMarkers.map((m) => (
          <span
            key={m.position}
            className="absolute hidden sm:inline text-[10px] text-wall-muted/50 -translate-x-1/2"
            style={{ left: `${m.position}%` }}
          >
            {m.label}
          </span>
        ))}
        <span className="absolute right-0 text-[9px] sm:text-[10px] text-wall-muted/70">
          {formatTime(maxTime)}
        </span>
      </div>
    </div>
  );
}
